// One inquiry: /growth/inquiries/:id
//
// The question, its status and Ops notes (each saves itself), Reply (an email back
// to them, InquiryReply.tsx) with the replies sent, and Convert to lead. One from
// the landing site also has its form's details and where they came from. A
// newsletter signup has no name and no message, so the page falls back to the address.

import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '@/api'
import { isApiError } from '@/api/client'
import { INQUIRY_STATUSES, type Inquiry, type InquiryStatus } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  CardHeader,
  ConfirmDialog,
  EditableSelectField,
  EditableTextField,
  EmptyState,
  ErrorState,
  KeyValueList,
  LoadingState,
  useToast,
} from '@/components/ui'
import { formatDateTime } from '@/lib/format'
import { INQUIRY_STATUS_LABELS } from '@/lib/growth'
import { VisitAttributionView } from './signupShared'
import { useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, setQueryData, useQuery } from '@/lib/query'
import { InquiryStatusBadge } from './shared'
import { InquiryConversation, InquiryReplyButton } from './InquiryReply'

const STATUS_OPTIONS = INQUIRY_STATUSES.map((s) => ({ value: s, label: INQUIRY_STATUS_LABELS[s] }))

/** The landing form's details, in its order. The organisation is shown above, the form is the topic. */
const DETAIL_ROWS: Array<[Exclude<keyof Inquiry['details'], 'form'>, string]> = [
  ['league_name', 'League'],
  ['sport', 'Sport'],
  ['teams', 'Teams'],
  ['when', 'When'],
  ['notes', 'Notes'],
]

export default function InquiryDetailPage() {
  const { id: rawId } = useParams()
  const id = Number(rawId)
  const validId = Number.isInteger(id) && id > 0
  const navigate = useNavigate()
  const toast = useToast()
  const [converting, setConverting] = useState(false)

  const { data: inquiry, error, status, refetch } = useQuery(validId ? ['growth', 'inquiry', id] : null, (signal) => api.growth.inquiries.get(id, signal))
  useDocumentTitle(inquiry ? `Inquiry from ${inquiry.name ?? inquiry.email}` : 'Inquiry')

  if (!validId || (status === 'error' && isApiError(error) && error.code === 'not_found')) {
    return (
      <Card>
        <EmptyState
          title="Inquiry not found"
          description={validId ? `There's no inquiry with id ${id}.` : `“${rawId}” isn't an inquiry id.`}
          action={<ButtonLink to="/growth/inquiries">Back to inquiries</ButtonLink>}
        />
      </Card>
    )
  }
  if (status === 'loading' && !inquiry) return <LoadingState label="Loading inquiry…" />
  if (!inquiry) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't load this inquiry" onRetry={refetch} />
      </Card>
    )
  }

  const save = async (patch: { status?: InquiryStatus; notes?: string | null }) => {
    const next: Inquiry = await api.growth.inquiries.update(inquiry.id, patch)
    setQueryData(['growth', 'inquiry', id], next)
    invalidateQueries('growth')
  }

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Inquiries', to: '/growth/inquiries' }, { label: inquiry.name ?? inquiry.email }]}
        title={inquiry.name ?? inquiry.email}
        subtitle={<span className="break-all">{inquiry.email}</span>}
        badges={
          <>
            <InquiryStatusBadge status={inquiry.status} />
            {inquiry.suppressed ? <Badge tone="bad">Do not contact</Badge> : null}
          </>
        }
        actions={
          <>
            <InquiryReplyButton inquiry={inquiry} />
            {inquiry.lead_id ? (
              <ButtonLink to={`/growth/leads/${inquiry.lead_id}`}>View lead</ButtonLink>
            ) : (
              <Button onClick={() => setConverting(true)}>Convert to lead</Button>
            )}
          </>
        }
      />

      <div className="grid items-start gap-4 lg:grid-cols-[1.2fr_1fr]">
        <div className="grid content-start gap-4">
          <Card>
            <CardHeader title="Message" />
            {inquiry.message ? (
              <p className="whitespace-pre-wrap break-words text-[13.5px] leading-relaxed text-ink">{inquiry.message}</p>
            ) : (
              <p className="text-[13.5px] text-muted">No message.</p>
            )}
          </Card>
          <InquiryConversation inquiry={inquiry} />
        </div>
        <div className="grid content-start gap-4">
          <Card>
            <CardHeader title="Details" />
            <KeyValueList
              items={[
                { label: 'Organisation', value: inquiry.organisation ?? '—' },
                { label: 'Topic', value: inquiry.topic },
                ...DETAIL_ROWS.filter(([k]) => inquiry.details?.[k]).map(([k, label]) => ({
                  label,
                  value: <span className="whitespace-pre-wrap break-words">{inquiry.details[k]}</span>,
                })),
                { label: 'Sent from', value: inquiry.via === 'landing' ? 'The landing site' : (inquiry.source_host ?? '—') },
                { label: 'Received', value: formatDateTime(inquiry.created_at) },
              ]}
            />
          </Card>
          {inquiry.via === 'landing' || inquiry.attribution ? (
            <Card>
              <CardHeader title="Where they came from" description="As the landing page reported it." />
              <VisitAttributionView attribution={inquiry.attribution} />
            </Card>
          ) : null}
          <Card>
            <CardHeader title="Handling" />
            <div className="grid gap-4">
              <EditableSelectField label="Status" value={inquiry.status} options={STATUS_OPTIONS} onSave={(v) => save({ status: v as InquiryStatus })} />
              <EditableTextField label="Notes" value={inquiry.notes ?? ''} multiline hint="Only Ops sees these." onSave={(v) => save({ notes: v || null })} />
            </div>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={converting}
        onClose={() => setConverting(false)}
        title="Convert to lead?"
        description="This makes a lead from this question, with the details filled in. If this email is already a lead, the inquiry links to it instead. Nothing is sent to anyone."
        confirmLabel="Convert to lead"
        tone="primary"
        onConfirm={async () => {
          const out = await api.growth.inquiries.convert(inquiry.id)
          invalidateQueries('growth')
          toast.success(out.created ? 'Lead created.' : 'Linked to the existing lead.')
          navigate(`/growth/leads/${out.lead.id}`)
        }}
      />
    </div>
  )
}
